import React, {FC, useEffect, useMemo, useState} from 'react'
import {toast} from 'react-toastify'
import {KTSVG} from '../../../../_metronic/helpers'
import {useAuth} from '../../auth'
import Module1ReportNameModal from './Module1ReportNameModal'
import {useMonthEndFeatureAccess} from '../core/monthEndAccess'
import QhseAccessDenied from './QhseAccessDenied'
import SearchableSelect from '../../../shared/components/SearchableSelect'

const API_URL = process.env.REACT_APP_API_URL

type ReportNameItem = {
  id: number
  name: string
  companyGroupAdminId?: number | null
  companyGroupName?: string | null
  isActive?: boolean | null
}

type CompanyGroup = {id: number; name: string}

const Module1ReportLibraryModal: FC<{isOpen: boolean; onClose: () => void}> = ({isOpen, onClose}) => {
  const {auth} = useAuth()
  const {canRead, canCreate, canDelete, assignedCompanyGroupId, isGlobalScope} =
    useMonthEndFeatureAccess('module1-report')

  const [rows, setRows] = useState<ReportNameItem[]>([])
  const [companies, setCompanies] = useState<CompanyGroup[]>([])
  const [search, setSearch] = useState('')
  const [companyGroupIdFilter, setCompanyGroupIdFilter] = useState('')
  const [loading, setLoading] = useState(false)
  const [isAddReportNameOpen, setIsAddReportNameOpen] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    fetchCompanies()
    fetchLibrary()
  }, [isOpen, companyGroupIdFilter, search])

  const fetchCompanies = async () => {
    if (!isGlobalScope) return
    try {
      const res = await fetch(`${API_URL}/company-group-admins`, {
        headers: {Authorization: `Bearer ${auth?.auth?.jwt}`},
      })
      if (!res.ok) throw new Error('Failed to load companies')
      const data = await res.json()
      setCompanies((Array.isArray(data) ? data : []).map((c: any) => ({id: c.id, name: c.name})))
    } catch (e: any) {
      toast.error('Failed to load companies')
    }
  }

  const fetchLibrary = async () => {
    if (!auth?.auth?.jwt) return
    if (isGlobalScope && !companyGroupIdFilter) {
      setRows([])
      return
    }
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (search.trim()) params.append('search', search.trim())
      params.append('page', '0')
      params.append('size', '1000')
      params.append('activeOnly', 'true')
      if (isGlobalScope && companyGroupIdFilter) {
        params.append('companyGroupId', companyGroupIdFilter)
      } else if (assignedCompanyGroupId != null) {
        params.append('companyGroupId', String(assignedCompanyGroupId))
      }

      const res = await fetch(`${API_URL}/qhse/module1-report-names/library?${params.toString()}`, {
        headers: {Authorization: `Bearer ${auth?.auth?.jwt}`},
      })
      if (!res.ok) throw new Error('Failed to load report names')
      const data = await res.json()
      const content = Array.isArray(data) ? data : data.content || []
      setRows(
        content.map((item: any) => ({
          id: item.id,
          name: item.name,
          companyGroupAdminId: item.companyGroupAdminId,
          companyGroupName: item.companyGroupName,
          isActive: item.isActive,
        }))
      )
    } catch (e: any) {
      toast.error('Failed to load report names')
      setRows([])
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this report name?')) return
    try {
      const res = await fetch(`${API_URL}/qhse/module1-report-names/${id}`, {
        method: 'DELETE',
        headers: {Authorization: `Bearer ${auth?.auth?.jwt}`},
      })
      if (!res.ok) throw new Error('Failed to delete report name')
      toast.success('Report name deleted successfully')
      fetchLibrary()
    } catch (e: any) {
      toast.error('Failed to delete report name')
    }
  }

  const companyNameById = useMemo(() => {
    const map = new Map<number, string>()
    companies.forEach((c) => map.set(c.id, c.name))
    return map
  }, [companies])

  if (!isOpen) return null
  if (!canRead) {
    return (
      <div className='modal fade show d-block' tabIndex={-1} style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
        <div className='modal-dialog modal-dialog-centered modal-xl'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h5 className='modal-title'>Report Library</h5>
              <button type='button' className='btn-close' onClick={onClose} />
            </div>
            <div className='modal-body'>
              <QhseAccessDenied message='Access denied — you do not have permission to view the Month End Report Library.' />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='modal fade show d-block' tabIndex={-1} style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
      <div className='modal-dialog modal-dialog-centered modal-xl'>
        <div className='modal-content'>
          <div className='modal-header'>
            <h5 className='modal-title'>Report Library</h5>
            <button type='button' className='btn-close' onClick={onClose} />
          </div>

          <div className='modal-body'>
            <div className='row g-3 align-items-end mb-4'>
              <div className='col-md-4'>
                <label className='form-label fw-semibold fs-6 mb-2 text-gray-700'>Search</label>
                <input
                  type='text'
                  className='form-control form-control-solid'
                  placeholder='Search report name...'
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              {isGlobalScope && (
                <div className='col-md-3'>
                  <label className='form-label fw-semibold fs-6 mb-2 text-gray-700'>Company</label>
                  <SearchableSelect
                    className='form-select form-select-solid'
                    value={companyGroupIdFilter}
                    onChange={(e) => setCompanyGroupIdFilter(e.target.value)}
                  >
                    <option value=''>Select company</option>
                    {companies.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </SearchableSelect>
                </div>
              )}

              <div className='col-md-auto ms-auto'>
                <button
                  className='btn btn-primary'
                  type='button'
                  onClick={() => setIsAddReportNameOpen(true)}
                  disabled={!canCreate}
                >
                  Add Report Name
                </button>
              </div>
            </div>

            <div className='table-responsive'>
              <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-3'>
                <thead>
                  <tr className='fw-bold text-muted'>
                    <th style={{width: '70px'}} className='text-center'>SR/NO</th>
                    <th>Report Name</th>
                    <th>Company</th>
                    <th className='text-center'>Active</th>
                    {canDelete && <th className='text-center min-w-100px'>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={canDelete ? 5 : 4} className='text-center py-6'>Loading...</td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={canDelete ? 5 : 4} className='text-center py-6'>No report names found</td>
                    </tr>
                  ) : (
                    rows.map((row, idx) => (
                      <tr key={row.id}>
                        <td className='text-center'>{idx + 1}</td>
                        <td>{row.name}</td>
                        <td>{row.companyGroupName || (row.companyGroupAdminId ? `#${row.companyGroupAdminId}` : '-')}</td>
                        <td className='text-center'>{row.isActive ? 'Yes' : 'No'}</td>
                        {canDelete && (
                          <td className='text-center'>
                            <button
                              className='btn btn-icon btn-bg-light btn-sm'
                              onClick={() => handleDelete(row.id)}
                              title='Delete Report Name'
                            >
                              <KTSVG path='/media/icons/duotune/general/gen027.svg' className='svg-icon-3 text-danger' />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className='modal-footer'>
            <button className='btn btn-light' onClick={onClose}>Close</button>
          </div>
        </div>
      </div>

      <Module1ReportNameModal
        isOpen={isAddReportNameOpen}
        onClose={() => setIsAddReportNameOpen(false)}
        companyGroupId={isGlobalScope ? companyGroupIdFilter : assignedCompanyGroupId?.toString()}
        onSaved={() => {
          fetchLibrary()
        }}
      />
    </div>
  )
}

export default Module1ReportLibraryModal





